import React, { useEffect, useState } from 'react';
import { Alert, DisplayInactiveToggle, PageHeader, ListPage } from '@/components/ui';
import FiltersCard from '../../../components/FiltersCard';
import { DataSourceItem, DataSourceRowGroup, DataSourceType } from '../../../types/DataSource';
import { dataSourcesService } from '../../../services/dataSourcesService';
import DataSourcesTable, { DataSourceColumn } from '../components/DataSourcesTable';
import DataSourceModal, { DataSourceFormData, DataSourceFormField } from '../components/DataSourceModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { describeActionError } from '../../../utils/loadError';

/** sessionStorage page key for these pages' remembered search (V2-024), one entry per source. */
const LIST_KEY = 'referenceData';

/** "Code or Name", "Field, Text, or Code": what the search matches, from the page's columns. */
const searchPlaceholder = (columns: DataSourceColumn[]): string => {
  const labels = columns.map((c) => c.label);
  if (labels.length <= 1) return labels[0] ?? 'Search';
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
};

interface DataSourcesPageProps {
  title: string;
  source: DataSourceType;
  columns: DataSourceColumn[];
  fields: DataSourceFormField[];
  addButtonLabel: string;
  itemLabel: string;
}

const DataSourcesPage: React.FC<DataSourcesPageProps> = ({
  title,
  source,
  columns,
  fields,
  addButtonLabel,
  itemLabel
}) => {
  const [items, setItems] = useState<DataSourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // The search is kept for the sign-in, per reference list (V2-024).
  const [searchTerm, setSearchTerm] = usePersistedListState(LIST_KEY, `${source}.search`, '');
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DataSourceItem | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [rowGroups, setRowGroups] = useState<DataSourceRowGroup[]>([]);
  const { hasPermission } = usePermissions();

  const canView = hasPermission('data_sources:view');
  const canEdit = hasPermission('data_sources:edit');
  const canDelete = hasPermission('data_sources:delete');

  const fetchDataSources = async () => {
    try {
      setLoading(true);
      const data = await dataSourcesService.getDataSources({
        source,
        search: searchTerm || undefined,
        includeInactive: includeInactive || undefined
      });
      setItems(data);
    } catch (error) {
      console.error('Error loading data sources:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchDataSources();
    }
  }, [source, searchTerm, includeInactive, canView]);

  // Whether any field is a picker that needs the server's list of questions. Kept as a
  // primitive because `fields` is an inline array literal in each wrapper page: depending on
  // the array itself would re-run the effect, and refetch, every time a wrapper re-renders.
  const needsRowGroups = fields.some(field => field.control === 'select');

  // The questions this datasource serves, for the `type` picker. Fetched once per source:
  // it is derived from code on the server and does not change while the page is open.
  useEffect(() => {
    if (!canView || !needsRowGroups) return;
    dataSourcesService
      .getRowGroups(source)
      .then(setRowGroups)
      .catch(error => console.error('Error loading option groups:', error));
  }, [source, canView, needsRowGroups]);

  const handleSearch = (value: string) => {
    setSearchInput(value.trim());
    setSearchTerm(value.trim());
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item: DataSourceItem) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDeleteRequest = (item: DataSourceItem) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleSave = async (data: DataSourceFormData) => {
    // Every key is gated on whether this source actually has that field. `code` and `name`
    // used to be sent unconditionally, which is wrong for rockfall: its options have no code,
    // and sending an empty one would fail validation on a field the form never showed.
    const fieldKeys = new Set(fields.map(field => field.key));
    const forField = <T,>(key: DataSourceFormField['key'], value: T) =>
      fieldKeys.has(key) ? value : undefined;

    const payload = {
      code: forField('code', data.code),
      name: forField('name', data.name),
      type: forField('type', data.type),
      property: forField('property', data.property),
      label: forField('label', data.label),
      sortOrder: forField('sortOrder', data.sortOrder === '' ? 0 : Number(data.sortOrder)),
      isActive: forField('isActive', data.isActive)
    };

    if (selectedItem) {
      await dataSourcesService.updateDataSource(selectedItem.id, payload);
    } else {
      await dataSourcesService.createDataSource({
        source,
        ...payload
      });
    }
    await fetchDataSources();
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    setDeleteError(null);
    try {
      setIsDeleting(true);
      await dataSourcesService.deleteDataSource(selectedItem.id);
      await fetchDataSources();
    } catch (error) {
      console.error('Error deleting data source:', error);
      setDeleteError(describeActionError(error, `Couldn't delete the ${itemLabel.toLowerCase()}. Try again.`));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSelectedItem(null);
    }
  };

  if (!canView) {
    return (
      <ListPage>
        <PageHeader title={title} />
        <Alert variant="warning">You do not have access to this page.</Alert>
      </ListPage>
    );
  }

  return (
    <ListPage>
      <PageHeader
        title={title}
        actions={[
          {
            key: 'add',
            label: addButtonLabel,
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canEdit,
            onClick: handleAdd,
          },
        ]}
      />

      {/* The shared filter panel with the shared search field in its header bar (V2-077,
          SRCH-06). Its one filter is Display Inactive: retired options are hidden by default,
          matching what the field app sees, but an admin still needs them – a historic entry
          resolves its label from a retired row. */}
      <FiltersCard
        title={`${title} Filters`}
        onClear={() => {
          setIncludeInactive(false);
          handleSearch('');
        }}
        hasActiveFilters={searchTerm !== '' || includeInactive}
        appliedFilterCount={includeInactive ? 0 : 1}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: searchPlaceholder(columns),
          label: `Search ${title}`,
        }}
      >
        <DisplayInactiveToggle id="data-sources-include-inactive" checked={includeInactive} onChange={setIncludeInactive} />
      </FiltersCard>

      {deleteError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setDeleteError(null)} className="tw-mb-3">
          {deleteError}
        </Alert>
      )}

      <ListPage.Body aria-busy={loading}>
        <DataSourcesTable
          items={items}
          loading={loading}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      </ListPage.Body>

      <DataSourceModal
        visible={showModal}
        title={selectedItem ? `Edit ${itemLabel}` : `Add ${itemLabel}`}
        fields={fields}
        rowGroups={rowGroups}
        initialData={
          selectedItem
            ? {
                code: selectedItem.code ?? '',
                name: selectedItem.name,
                type: selectedItem.type ?? '',
                property: selectedItem.property ?? '',
                label: selectedItem.label ?? '',
                sortOrder: String(selectedItem.sortOrder ?? ''),
                isActive: selectedItem.isActive !== false
              }
            : undefined
        }
        onClose={() => setShowModal(false)}
        onSave={handleSave}
      />

      <DeleteConfirmModal
        visible={showDeleteModal}
        title={`Delete ${itemLabel}`}
        message={`Are you sure you want to delete this ${itemLabel.toLowerCase()}?`}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </ListPage>
  );
};

export default DataSourcesPage;
